import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { useWorkflow } from "../../context/WorkflowContext";
import { useChatContext } from "../ChatContext";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@shadcn-in-nx/ui/tooltip";
import {
    GitBranch,
    Plus,
    Lock,
    Unlock,
    Loader2,
    ArrowUpRight,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

// Environment type badges
const ENVIRONMENT_BADGES = {
    DEVELOPMENT: { label: "Dev", color: "bg-blue-100 text-blue-700" },
    STAGING: { label: "Stage", color: "bg-yellow-100 text-yellow-700" },
    PRODUCTION: { label: "Prod", color: "bg-green-100 text-green-700" },
    CUSTOM: { label: "Custom", color: "bg-purple-100 text-purple-700" },
};

export default function EnvironmentSelector({
    selectedEnvironment,
    onEnvironmentChange,
    onCreateEnvironment,
    onPromoteEnvironment,
    showLockStatus = true,
}) {
    const { t } = useTranslation();
    const { isFeatureEnabled, getCustomPlanNumberFeature } = useAuth();
    const { workflow, refetchWorkflow } = useWorkflow();
    const { environments, fetchEnvironments, isLoadingEnvironments } =
        useChatContext();

    const [isLoading, setIsLoading] = useState(false);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [newEnvironmentName, setNewEnvironmentName] = useState("");
    const [isCreating, setIsCreating] = useState(false);

    const { call: createEnvironment } = useBluelagoon({
        method: "createWorkflowEnvironment",
    });

    const maxEnvironments = getCustomPlanNumberFeature("enviroments");
    const currentEnvironments = environments.length;
    const isEnvironmentLimitReached = currentEnvironments >= maxEnvironments;

    const handleCreateEnvironment = async () => {
        if (isEnvironmentLimitReached) {
            toast.error(
                t("environments.limitReached") ||
                    `You've reached your plan limit of ${maxEnvironments} environment${
                        maxEnvironments !== 1 ? "s" : ""
                    }. Upgrade your plan to create more environments.`
            );
            return;
        }

        if (!newEnvironmentName?.trim()) {
            toast.error(
                t("environments.nameRequired") || "Environment name is required"
            );
            return;
        }

        setIsCreating(true);
        try {
            const result = await createEnvironment({
                workflowId: workflow.id,
                name: newEnvironmentName?.trim().toLowerCase(),
            });

            if (result?.environment) {
                toast.success(
                    t("environments.created") ||
                        "Environment created successfully"
                );
                fetchEnvironments();
                onEnvironmentChange?.(result.environment.name);
                setIsCreateDialogOpen(false);
                setNewEnvironmentName("");
                onCreateEnvironment?.(result.environment);
            }
        } catch (error) {
            toast.error(
                error.message ||
                    t("environments.createFailed") ||
                    "Failed to create environment"
            );
        } finally {
            setIsCreating(false);
        }
    };

    const currentEnvironment = selectedEnvironment
        ? environments.find((e) => e.name === selectedEnvironment)
        : null;
    // If no environment is selected, default to production
    const isProduction =
        !selectedEnvironment || currentEnvironment?.type === "PRODUCTION";
    const badge = isProduction
        ? ENVIRONMENT_BADGES.PRODUCTION
        : ENVIRONMENT_BADGES[currentEnvironment?.type] ||
          ENVIRONMENT_BADGES.CUSTOM;
    const isLocked = workflow?.isProductionLocked;

    if (!isFeatureEnabled("enviroments")) {
        return null;
    }

    if (isLoading && environments.length === 0) {
        return (
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg px-1 py-0.5 text-gray-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="text-[10px] hidden lg:inline">
                    {t("common.loading") || "Loading..."}
                </span>
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center gap-0.5 lg:gap-1">
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="flex items-center bg-gray-100 rounded-lg px-2 py-1">
                                {/* Icon with badge color indicator */}
                                <div className="relative flex-shrink-0">
                                    <GitBranch className="w-3 h-3 text-gray-500" />
                                    {/* Small colored dot indicator - visible on small screens */}
                                </div>
                                <Select
                                    value={selectedEnvironment || "production"}
                                    onValueChange={(value) => {
                                        // If "production" is selected, pass null/undefined to indicate default production
                                        onEnvironmentChange?.(
                                            value === "production"
                                                ? null
                                                : value
                                        );
                                    }}
                                >
                                    <SelectTrigger className="border-0 bg-transparent h-5 focus:ring-0 gap-0 p-0 pl-0.5 w-4 lg:w-auto lg:min-w-[60px] lg:pl-0.5 lg:pr-0 text-xs">
                                        <span className="hidden lg:inline truncate max-w-[80px]">
                                            <SelectValue
                                                placeholder={
                                                    t("environments.select") ||
                                                    "Select"
                                                }
                                            />
                                        </span>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {/* Production option (doesn't exist as a real environment) */}
                                        <SelectItem value="production">
                                            <div className="flex items-center gap-1.5 text-xs">
                                                <span className="text-xs">
                                                    production
                                                </span>
                                                <span
                                                    className={`text-[10px] px-1 py-0.5 rounded ${ENVIRONMENT_BADGES.PRODUCTION.color}`}
                                                >
                                                    {
                                                        ENVIRONMENT_BADGES
                                                            .PRODUCTION.label
                                                    }
                                                </span>
                                                {isLocked && (
                                                    <Lock className="w-2.5 h-2.5 text-orange-500" />
                                                )}
                                            </div>
                                        </SelectItem>
                                        {environments.length > 0 && (
                                            <div className="border-t border-gray-100 my-1" />
                                        )}
                                        {environments.map((env) => {
                                            const envBadge =
                                                ENVIRONMENT_BADGES[env.type] ||
                                                ENVIRONMENT_BADGES.CUSTOM;
                                            return (
                                                <SelectItem
                                                    key={env.id}
                                                    value={env.name}
                                                >
                                                    <div className="flex items-center gap-1.5 text-xs">
                                                        <span className="text-xs">
                                                            {env.name}
                                                        </span>
                                                        <span
                                                            className={`text-[10px] px-1 py-0.5 rounded ${envBadge.color}`}
                                                        >
                                                            {envBadge.label}
                                                        </span>
                                                        {env.type ===
                                                            "PRODUCTION" &&
                                                            isLocked && (
                                                                <Lock className="w-2.5 h-2.5 text-orange-500" />
                                                            )}
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                        <div className="border-t border-gray-100 my-0.5" />
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        onClick={() => {
                                                            if (
                                                                !isEnvironmentLimitReached
                                                            ) {
                                                                setIsCreateDialogOpen(
                                                                    true
                                                                );
                                                            }
                                                        }}
                                                        disabled={
                                                            isEnvironmentLimitReached
                                                        }
                                                        className={`w-full flex items-center gap-1.5 px-1.5 py-1 text-xs rounded ${
                                                            isEnvironmentLimitReached
                                                                ? "text-gray-400 cursor-not-allowed"
                                                                : "text-gray-600 hover:bg-gray-100"
                                                        }`}
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                        {t(
                                                            "environments.create"
                                                        ) ||
                                                            "Create environment"}
                                                    </button>
                                                </TooltipTrigger>
                                                {isEnvironmentLimitReached && (
                                                    <TooltipContent>
                                                        <p>
                                                            {t(
                                                                "environments.limitReached"
                                                            ) ||
                                                                `You've reached your plan limit of ${maxEnvironments} environment${
                                                                    maxEnvironments !==
                                                                    1
                                                                        ? "s"
                                                                        : ""
                                                                }. Upgrade your plan to create more environments.`}
                                                        </p>
                                                    </TooltipContent>
                                                )}
                                            </Tooltip>
                                        </TooltipProvider>
                                    </SelectContent>
                                </Select>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent className="lg:hidden">
                            {selectedEnvironment
                                ? currentEnvironment?.name ||
                                  t("environments.select") ||
                                  "Select environment"
                                : "production"}
                        </TooltipContent>
                    </Tooltip>

                    {/* Production lock status - only show when in production */}
                    {showLockStatus && isProduction && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div
                                    className={`hidden lg:flex p-1 rounded-md ${
                                        isLocked
                                            ? "bg-orange-100 text-orange-600"
                                            : "bg-gray-100 text-gray-500"
                                    }`}
                                >
                                    {isLocked ? (
                                        <Lock className="w-3 h-3" />
                                    ) : (
                                        <Unlock className="w-3 h-3" />
                                    )}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                {isLocked
                                    ? t("environments.productionLocked") ||
                                      "Production is locked"
                                    : t("environments.productionUnlocked") ||
                                      "Production is unlocked"}
                            </TooltipContent>
                        </Tooltip>
                    )}

                    {/* Promote button - only show when not in production */}
                    {!isProduction && onPromoteEnvironment && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={() =>
                                        onPromoteEnvironment(
                                            selectedEnvironment
                                        )
                                    }
                                    disabled={isLocked}
                                    className="hidden lg:flex p-1 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ArrowUpRight className="w-3 h-3" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {isLocked
                                    ? t("environments.productionLocked") ||
                                      "Production is locked"
                                    : t("environments.promoteToProduction") ||
                                      "Promote to production"}
                            </TooltipContent>
                        </Tooltip>
                    )}

                </TooltipProvider>
            </div>

            {/* Create Environment Dialog */}
            <Dialog
                open={isCreateDialogOpen}
                onOpenChange={(open) => {
                    setIsCreateDialogOpen(open);
                    if (!open) {
                        setNewEnvironmentName("");
                    }
                }}
            >
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>
                            {t("environments.createNew") ||
                                "Create New Environment"}
                        </DialogTitle>
                        <DialogDescription>
                            {t("environments.createDescription") ||
                                "Create a new environment for your workflow. Environments allow you to have separate development, staging, and production versions."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="environment-name">
                                {t("environments.name") || "Environment Name"}
                            </Label>
                            <Input
                                id="environment-name"
                                value={newEnvironmentName}
                                onChange={(e) =>
                                    setNewEnvironmentName(e.target.value)
                                }
                                placeholder={
                                    t("environments.namePlaceholder") ||
                                    "e.g., staging, qa, testing"
                                }
                                disabled={isCreating}
                            />
                            <p className="text-xs text-gray-500">
                                {t("environments.nameHelp") ||
                                    "Use lowercase letters, numbers, and hyphens only."}
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsCreateDialogOpen(false)}
                            disabled={isCreating}
                        >
                            {t("common.cancel") || "Cancel"}
                        </Button>
                        <Button
                            onClick={handleCreateEnvironment}
                            disabled={isCreating || !newEnvironmentName?.trim()}
                        >
                            {isCreating ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t("common.creating") || "Creating..."}
                                </>
                            ) : (
                                t("common.create") || "Create"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
